import { Component, inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  BehaviorSubject,
  combineLatest,
  filter,
  skip,
  switchMap,
  debounceTime,
} from 'rxjs'
//PRIMENG
import { TableLazyLoadEvent, TableModule } from 'primeng/table'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { CardModule } from 'primeng/card'
import { TabsModule } from 'primeng/tabs'
import { DropdownModule } from 'primeng/dropdown'
import { ChipModule } from 'primeng/chip'
import { TagModule } from 'primeng/tag'
import { BadgeModule } from 'primeng/badge'
import { TooltipModule } from 'primeng/tooltip'
import { ToolbarModule } from 'primeng/toolbar'
import { SkeletonModule } from 'primeng/skeleton'
import { IconFieldModule } from 'primeng/iconfield'

//APP
import { MlSocieties } from '../../../../core/enums/ml-societies'
import { InterventionsService } from '../../services/interventions.service'
import { RouterLink, Router } from '@angular/router'
import { environment } from '../../../../../environments/environment'
import { Select } from 'primeng/select'
import { UserModel } from '../../../../core/models/user.model'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-interventions-incoming',
  standalone: true,
  imports: [
    TableModule,
    CommonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    ReactiveFormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    CardModule,
    TabsModule,
    RouterLink,
    DropdownModule,
    ChipModule,
    TagModule,
    BadgeModule,
    TooltipModule,
    ToolbarModule,
    SkeletonModule,
    Select,
    FormsModule,
  ],
  templateUrl: './interventions-incoming.component.html',
  styleUrl: './interventions-incoming.component.scss',
})
export class InterventionsIncomingComponent implements OnInit {
  private interventionsService = inject(InterventionsService)
  private router = inject(Router)

  private tableLazyLoad$ = new BehaviorSubject<TableLazyLoadEvent | null>(null)
  private selectedMlSocietyId = new BehaviorSubject<String | null>(null)

  private allInterventions: any[] = []
  private filteredInterventions: any[] = []

  public allInterventionStatus: any[] = []
  public selectedInterventionStatus: string | null = null

  public allAssistants: any[] = []
  public allTechnicians: any[] = []
  public allProducts: any[] = []
  public selectedAssistant: string | null = null
  public selectedTechnician: string | null = null
  public selectedStatus: string | null = null
  public selectedProduct: string | null = null

  public filtersOpened: boolean = false

  public incomingInterventions$ = new BehaviorSubject<any[]>([])
  public totalRecords$ = new BehaviorSubject<number>(0)
  public loading$ = this.interventionsService.incomingLoading$

  public societyCounts: Record<string, number> = {}

  getCurrentSocietyCount(): number {
    return this.societyCounts[this._activeTab] || 0
  }

  public sortOptions = [
    { label: 'Date (croissant)', value: 'startDate_asc' },
    { label: 'Date (décroissant)', value: 'startDate_desc' },
    { label: 'Nom (A-Z)', value: 'name_asc' },
    { label: 'Nom (Z-A)', value: 'name_desc' },
    { label: 'Client (A-Z)', value: 'customer_asc' },
    { label: 'Client (Z-A)', value: 'customer_desc' },
  ]

  mlSocieties = Object.values(MlSocieties).map((society) => {
    return {
      id: society,
      label: society,
      icon: 'tll tll-24 fa-logo-' + society,
    }
  })

  public searchInput = new FormControl('')
  public _activeTab: string = this.mlSocieties[0].id
  private initialized = false

  public activeTabIndex: number = 0
  private _previousTab: string | null = null

  private columnFilters: Record<string, string> = {}

  get activeTab() {
    return this._activeTab
  }

  set activeTab(value: string) {
    this.selectedMlSocietyId.next(value)
  }

  constructor() {
    this.selectedMlSocietyId
      .pipe(
        filter((society) => society !== null),
        debounceTime(50),
        takeUntilDestroyed(),
        switchMap((mlSociety) => {
          return this.interventionsService.getIncoming({
            erpSource: mlSociety!,
          })
        })
      )
      .subscribe((response) => {
        if (response) {
          this.allInterventions = response
          this.rebuildFiltersAndOptions()
          this.setFiltersFromLocalStorage()
          this.applyFilters()
        } else {
          console.error('Invalid response structure:', response)
        }
      })

    this.tableLazyLoad$
      .pipe(
        filter((event) => event !== null && this.initialized),
        debounceTime(50),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.applyFilters()
      })
  }

  ngOnInit() {
    this.initialized = true
    this.initializeData()
  }

  initializeData() {
    const defaultSociety = this.mlSocieties[0].id
    const defaultEvent: TableLazyLoadEvent = { first: 0, rows: 10 }

    this.tableLazyLoad$.next(defaultEvent)

    this.loadAllSocietyCounts().then(() => {
      this.selectedMlSocietyId.next(defaultSociety)
    })
  }

  private async loadAllSocietyCounts(): Promise<void> {
    const promises = this.mlSocieties.map((society) =>
      this.interventionsService
        .getIncoming({
          erpSource: society.id,
        })
        .toPromise()
        .then((response) => {
          if (response) {
            this.societyCounts[society.id] = response.length
          }
        })
        .catch((error) => {
          console.error(`Error loading data for ${society.id}:`, error)
          this.societyCounts[society.id] = 0
        })
    )

    await Promise.all(promises)
  }

  private rebuildFiltersAndOptions() {
    this.selectedInterventionStatus = null
    this.selectedAssistant = null
    this.selectedTechnician = null
    this.selectedStatus = null
    this.selectedProduct = null

    this.allInterventionStatus = Array.from(
      new Set(this.allInterventions.map((item) => item.interventionStatus.name))
    ).map((type) => ({
      label: type,
      value: type,
    }))

    this.allProducts = Array.from(
      new Set(
        this.allInterventions.flatMap((item) =>
          item.affairProducts.map(
            (prod: { product: { name: any } }) => prod.product.name
          )
        )
      )
    ).map((name) => ({
      label: name,
      value: name,
    }))

    this.allAssistants = Array.from(
      new Set(
        this.allInterventions
          .filter((item) => item.userAssistant)
          .map(
            (item) =>
              `${item.userAssistant.firstname} ${item.userAssistant.lastname}`
          )
      )
    ).map((name) => ({
      label: name,
      value: name,
    }))

    const technicianNames = new Set<string>()
    this.allInterventions.forEach((item) => {
      if (Array.isArray(item.usersTechnicians)) {
        item.usersTechnicians.forEach((tech: UserModel) => {
          technicianNames.add(`${tech.firstname} ${tech.lastname}`)
        })
      }
    })
    this.allTechnicians = Array.from(technicianNames).map((name) => ({
      label: name,
      value: name,
    }))
  }

  protected applyFilters() {
    const event = this.tableLazyLoad$.getValue()
    if (!event) {
      return
    }

    let filtered = [...this.allInterventions]
    const searchTerm = this.searchInput.getRawValue()?.toLowerCase()

    if (searchTerm) {
      filtered = filtered.filter(
        (intervention) =>
          (intervention.name?.toLowerCase() || '').includes(searchTerm) ||
          (intervention.affairMlRef?.toLowerCase() || '').includes(
            searchTerm
          ) ||
          (
            intervention.affairFinalsCustomersFullNames?.toLowerCase() || ''
          ).includes(searchTerm) ||
          (intervention.affairWorkSiteLocality?.toLowerCase() || '').includes(
            searchTerm
          )
      )
    }

    if (this.selectedInterventionStatus) {
      filtered = filtered.filter(
        (item) =>
          item.interventionStatus.name === this.selectedInterventionStatus
      )

      localStorage.setItem(
        'selectedInterventionStatus',
        this.selectedInterventionStatus
      )
    }

    if (this.selectedProduct) {
      filtered = filtered.filter((item) =>
        item.affairProducts.some(
          (prod: { product: { name: string | null } }) =>
            prod.product.name === this.selectedProduct
        )
      )

      localStorage.setItem('selectedProduct', this.selectedProduct)
    }


    if (this.selectedAssistant) {
      filtered = filtered.filter((item) => {
        if (!item.userAssistant) return false
        const fullname = `${item.userAssistant.firstname} ${item.userAssistant.lastname}`
        return fullname === this.selectedAssistant
      })

      localStorage.setItem('selectedAssistant', this.selectedAssistant)
    }

    if (this.selectedTechnician) {
      filtered = filtered.filter((item) => {
        if (!Array.isArray(item.usersTechnicians)) return false
        return item.usersTechnicians.some((tech: UserModel) => {
          const fullname = `${tech.firstname} ${tech.lastname}`
          return fullname === this.selectedTechnician
        })
      })

      localStorage.setItem('selectedTechnician', this.selectedTechnician)
    }

    if (this.selectedStatus) {
      filtered = filtered.filter(
        (item) => item.interventionStatus.name === this.selectedStatus
      )

      localStorage.setItem('selectedStatus', this.selectedStatus)
    }

    Object.entries(this.columnFilters).forEach(([field, value]) => {
      if (value) {
        const filterValue = value.toLowerCase()

        if (field === 'address') {
          filtered = filtered.filter((intervention) => {
            const postalCode = String(
              intervention.affairWorkSitePostalCode || ''
            ).toLowerCase()
            const locality = String(
              intervention.affairWorkSiteLocality || ''
            ).toLowerCase()
            return (
              postalCode.includes(filterValue) || locality.includes(filterValue)
            )
          })
        } else {
          filtered = filtered.filter((intervention) => {
            const fieldValue = this.getNestedValue(intervention, field)

            if (fieldValue === null || fieldValue === undefined) return false

            if (field === 'startDate' && fieldValue) {
              const dateStr = new Date(fieldValue).toLocaleDateString()
              return dateStr.includes(filterValue)
            }

            return String(fieldValue).toLowerCase().includes(filterValue)
          })
        }
      }
    })

    if (event.sortField) {
      filtered.sort((a, b) => {
        let sortField = Array.isArray(event.sortField)
          ? event.sortField[0]
          : event.sortField
        let valueA = this.getNestedValue(a, sortField!)
        let valueB = this.getNestedValue(b, sortField!)

        const result = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
        return event.sortOrder === 1 ? result : -result
      })
    }

    // Update the count for current society with filtered results
    this.societyCounts[this._activeTab] = filtered.length
    this.totalRecords$.next(filtered.length)
    this.filteredInterventions = filtered

    const first = event.first || 0
    const rows = event.rows || 10
    const pagedData = filtered.slice(first, first + rows)

    this.incomingInterventions$.next(pagedData)
  }

  private getNestedValue(obj: any, path: string): any {
    if (!path.includes('.')) return obj[path]
    return path
      .split('.')
      .reduce((prev, curr) => (prev ? prev[curr] : null), obj)
  }

  lazyLoadEmit(event: TableLazyLoadEvent) {
    if (this.initialized) {
      this.tableLazyLoad$.next(event)
      this.applyFilters()
    }
  }

  refreshData() {
    if (this.initialized) {
      this.tableLazyLoad$.next({ first: 0, rows: 10 })

      // Refresh count for current society and reload data
      this.interventionsService
        .getIncoming({
          erpSource: this.selectedMlSocietyId.getValue()!,
        })
        .subscribe((response) => {
          if (response) {
            this.allInterventions = response
            this.societyCounts[this._activeTab] = response.length
            this.applyFilters()
          }
        })
    }
  }

  onSearchChange(event: Event) {
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })

      this.applyFilters()
    }
  }

  onSortChange(event: any) {
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
        sortField: event.value.split('_')[0],
        sortOrder: event.value.endsWith('asc') ? 1 : -1,
      })
      this.applyFilters()
    }
  }

  onColumnFilter(event: Event, field: string) {
    const inputElement = event.target as HTMLInputElement
    this.columnFilters[field] = inputElement.value

    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })
      this.applyFilters()
    }
  }

  clearAllFilters() {
    this.columnFilters = {}

    const filterInputs = document.querySelectorAll(
      '.p-column-filter'
    ) as NodeListOf<HTMLInputElement>
    filterInputs.forEach((input) => {
      input.value = ''
    })

    this.searchInput.setValue('')

    // Clear select filters
    this.selectedAssistant = null
    this.selectedTechnician = null
    this.selectedStatus = null
    this.selectedInterventionStatus = null

    // Clear localStorage
    localStorage.removeItem('selectedInterventionStatus')

    this.tableLazyLoad$.next({
      ...this.tableLazyLoad$.getValue(),
      first: 0,
    })

    this.applyFilters()
  }

  navigateToIntervention(intervention: any) {
    const erpSource = intervention.erpSource
    if (erpSource === 'abm') {
      window.open(
        environment.abmUrl + 'interventions/' + intervention.id + '/edit',
        '_blank'
      )
    } else if (erpSource === 'nrj') {
      window.open(
        environment.nrjUrl + 'interventions/' + intervention.id + '/edit',
        '_blank'
      )
    }
  }

  viewInterventionDetails(intervention: any) {
    this.router.navigate([
      '/intervention',
      intervention.id,
      'view',
      intervention.erpSource,
    ])
  }

  getStatusStyle(status: any): { [key: string]: string } {
    if (!status || !status.color) {
      return { color: 'var(--text-color)' }
    }
    return {
      color: status.color,
      fontWeight: 'bold',
    }
  }

  getStatusName(status: any): string {
    console.log(status?.name)
    return status?.name || 'Non défini'
  }

  navigateToAffair(intervention: any) {
    window.open(`/affair/${intervention.affair.ml_ref}`, '_blank')
  }

  onActiveIndexChange(event: any) {
    if (!this.initialized) return

    const newTabId = this.mlSocieties[event].id

    if (this._previousTab !== newTabId) {
      this._previousTab = newTabId
      this._activeTab = newTabId

      // Reset filters when changing tab
      this.clearAllFilters()

      this.selectedMlSocietyId.next(newTabId)
      this.tableLazyLoad$.next({ first: 0, rows: 10 })
    }
  }

  resetFilters() {
    this.selectedInterventionStatus = null
    this.selectedAssistant = null
    this.selectedTechnician = null
    this.selectedStatus = null
    this.selectedProduct = null

    localStorage.removeItem('selectedInterventionStatus')
    localStorage.removeItem('selectedAssistant')
    localStorage.removeItem('selectedTechnician')
    localStorage.removeItem('selectedStatus')
    localStorage.removeItem('selectedProduct')


    this.applyFilters()
  }

  // Add methods for select filter changes
  onAssistantFilterChange() {
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })
      this.applyFilters()
    }
  }

  onTechnicianFilterChange() {
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })
      this.applyFilters()
    }
  }

  onStatusFilterChange() {
    if (this.initialized) {
      this.tableLazyLoad$.next({
        ...this.tableLazyLoad$.getValue(),
        first: 0,
      })
      this.applyFilters()
    }
  }

  setFiltersFromLocalStorage() {

    const interventionStatus = localStorage.getItem(
      'selectedInterventionStatus'
    )

    if (interventionStatus !== null) {
      this.selectedInterventionStatus = interventionStatus
      this.filtersOpened = true
    }

    // Set other filters from localStorage if needed
    const assistant = localStorage.getItem('selectedAssistant')
    if (assistant !== null) {
      this.selectedAssistant = assistant
      this.filtersOpened = true
    }

    const technician = localStorage.getItem('selectedTechnician')
    if (technician !== null) {
      this.selectedTechnician = technician
      this.filtersOpened = true
    }

    const status = localStorage.getItem('selectedStatus')
    if (status !== null) {
      this.selectedStatus = status
      this.filtersOpened = true
    }

    const product = localStorage.getItem('selectedProduct')
    if (product !== null) {
      this.selectedProduct = product
      this.filtersOpened = true
    }


    this.applyFilters()
  }

  getTechniciansNames(usersTechnicians: any): string {
    return (
      usersTechnicians
        ?.map(
          (u: { firstname: any; lastname: any }) =>
            `${u.firstname} ${u.lastname}`
        )
        .join(', ') || ''
    )
  }
}
